// ABOUTME: Who is Logging analytics card component showing adult behavior logging participation
// ABOUTME: Uses pie chart visualization to display relative contributions from family adults

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { familyAPI } from '../services/api';
import PieChartWithAvatars from './PieChartWithAvatars';
import { CHART_COLORS, SYSTEM_ENTITY_IDS, PieChartDataPoint, ChartEntity } from '../types/chart';

interface WhoIsLoggingCardProps {
  familyId: number;
  timeWindow: string;
}

interface AdultLoggingData {
  userId: number;
  name: string;
  profileImageUrl?: string;
  behaviorCount: number;
}

interface FamilyAnalyticsResponse {
  timeWindow: {
    startDate: string;
    endDate: string;
    timezone: string;
    label: string;
  };
  metrics: {
    adultsLogging?: AdultLoggingData[];
  };
}

// Use centralized color palette
const ADULT_COLORS = CHART_COLORS.ADULTS;

const WhoIsLoggingCard: React.FC<WhoIsLoggingCardProps> = ({ familyId, timeWindow }) => {
  const [data, setData] = useState<FamilyAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!familyId) return;
      
      try {
        setIsLoading(true);
        setError(null);
        const response = await familyAPI.getAnalytics(familyId, {
          metrics: ['adultsLogging'],
          timeWindow: timeWindow
        });
        setData(response as FamilyAnalyticsResponse);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [familyId, timeWindow]);

  if (isLoading) {
    return (
      <Card data-testid="who-is-logging-card">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
            <CircularProgress data-testid="who-is-logging-loading" />
            <Typography sx={{ ml: 2 }}>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="who-is-logging-card">
        <CardContent>
          <Alert severity="error">
            Failed to load analytics data
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const adultsLogging = data?.metrics?.adultsLogging || [];
  const isEmpty = adultsLogging.length === 0;

  // Convert data for pie chart with voice recording support
  const pieChartData: PieChartDataPoint[] = adultsLogging.map((adult, index) => ({
    name: adult.name,
    value: adult.behaviorCount,
    childId: adult.userId, // Using childId for compatibility
    id: adult.userId,
    profileImageUrl: adult.profileImageUrl,
    color: ADULT_COLORS[index % ADULT_COLORS.length],
    isSystemCategory: adult.userId === SYSTEM_ENTITY_IDS.VOICE_RECORDING
  }));

  // Create entities array for PieChartWithAvatars component
  const adultsForChart: ChartEntity[] = adultsLogging.map(adult => ({
    id: adult.userId,
    name: adult.name,
    profileImageUrl: adult.profileImageUrl,
    isSystemCategory: adult.userId === SYSTEM_ENTITY_IDS.VOICE_RECORDING
  }));

  const title = `Who is Logging? (${timeWindow})`;

  return (
    <Card data-testid="who-is-logging-card">
      <CardContent>
        {isEmpty ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {title}
            </Typography>
            <Typography color="text.secondary">
              No behaviors logged in this time period. Start tracking to see your family&apos;s progress!
            </Typography>
          </Box>
        ) : (
          <PieChartWithAvatars
            data={pieChartData}
            entities={adultsForChart}
            title={title}
            dataType="behaviors"
            entityType="adult"
            testId="who-is-logging-pie-chart"
          />
        )}
      </CardContent>
    </Card>
  );
};

export default WhoIsLoggingCard;